import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Divider,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Menu,
  Paper,
  Stack,
  Badge,
  Avatar,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Notifications as NotificationsIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  DeleteSweep as DeleteAllIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Verified as VerifiedIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  Support as SupportIcon,
  PriorityHigh as PriorityHighIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { 
  notificationApi, 
  Notification, 
  NotificationStats, 
  NotificationPagination,
  ADMIN_NOTIFICATION_TYPES 
} from '../services/notificationApi';

const NotificationsPage: React.FC = () => {
  // State
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'read' | 'unread'>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  
  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Create notification form
  const [createForm, setCreateForm] = useState({
    targetUserId: '',
    title: '',
    message: '',
    type: '',
    data: ''
  });

  // Bulk notification form
  const [bulkForm, setBulkForm] = useState({
    targetRole: 'user' as 'user' | 'seller' | 'admin',
    title: '',
    message: '',
    type: '',
    data: ''
  });

  const { enqueueSnackbar } = useSnackbar();

  // انواع نوتیفیکیشن‌های ادمین
  const notificationTypes = [
    { value: 'all', label: 'همه' },
    { value: ADMIN_NOTIFICATION_TYPES.NEW_USER_REGISTERED, label: 'ثبت‌نام کاربر جدید' },
    { value: ADMIN_NOTIFICATION_TYPES.NEW_SELLER_APPLICATION, label: 'درخواست فروشندگی' },
    { value: ADMIN_NOTIFICATION_TYPES.SELLER_VERIFICATION_REQUEST, label: 'درخواست تایید فروشنده' },
    { value: ADMIN_NOTIFICATION_TYPES.NEW_PRODUCT_PENDING, label: 'محصول در انتظار بررسی' },
    { value: ADMIN_NOTIFICATION_TYPES.NEW_WITHDRAWAL_REQUEST, label: 'درخواست برداشت' },
    { value: ADMIN_NOTIFICATION_TYPES.SYSTEM_ERROR, label: 'خطای سیستم' },
    { value: ADMIN_NOTIFICATION_TYPES.DISPUTE_RAISED, label: 'اختلاف مطرح شده' },
    { value: ADMIN_NOTIFICATION_TYPES.NEW_TICKET, label: 'تیکت جدید' },
    { value: ADMIN_NOTIFICATION_TYPES.TICKET_ESCALATED, label: 'ارجاع تیکت' },
    { value: ADMIN_NOTIFICATION_TYPES.PAYMENT_ISSUE, label: 'مشکل پرداخت' },
    { value: ADMIN_NOTIFICATION_TYPES.SECURITY_ALERT, label: 'هشدار امنیتی' },
    { value: ADMIN_NOTIFICATION_TYPES.BANK_ACCOUNT_VERIFICATION, label: 'تایید حساب بانکی' }
  ];

  const createNotificationTypes = notificationTypes.filter(type => type.value !== 'all');

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pagination: NotificationPagination = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        isRead: selectedStatus === 'all' ? undefined : selectedStatus === 'read'
      };

      const [notificationsResponse, statsResponse] = await Promise.all([
        notificationApi.getAdminPanelNotifications(pagination),
        notificationApi.getStats('admin_panel')
      ]);

      if (notificationsResponse.success && notificationsResponse.data) {
        setNotifications(notificationsResponse.data.notifications || []);
        setTotalPages(notificationsResponse.data.pagination?.totalPages || 1);
        setTotalCount(notificationsResponse.data.pagination?.total || 0);
      } else {
        setError(notificationsResponse.message);
      }

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (error: any) {
      setError(error.message || 'خطا در بارگذاری نوتیفیکیشن‌ها');
      enqueueSnackbar('خطا در بارگذاری نوتیفیکیشن‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedType, selectedStatus, enqueueSnackbar]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Handlers
  const handleSelectNotification = (notificationId: string) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(n => n._id));
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const response = await notificationApi.markAsRead(notificationId);
      if (response.success) {
        enqueueSnackbar('نوتیفیکیشن به عنوان خوانده شده علامت‌گذاری شد', { variant: 'success' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در علامت‌گذاری نوتیفیکیشن', { variant: 'error' });
    }
  };

  const handleMarkAsUnread = async (notificationId: string) => {
    try {
      const response = await notificationApi.markAsUnread(notificationId);
      if (response.success) {
        enqueueSnackbar('نوتیفیکیشن به عنوان خوانده نشده علامت‌گذاری شد', { variant: 'success' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در علامت‌گذاری نوتیفیکیشن', { variant: 'error' });
    }
  };

  const handleMarkSelectedAsRead = async () => {
    try {
      const response = await notificationApi.markMultipleAsRead(selectedNotifications);
      if (response.success) {
        enqueueSnackbar(`${response.data?.updated || 0} نوتیفیکیشن به عنوان خوانده شده علامت‌گذاری شد`, { variant: 'success' });
        setSelectedNotifications([]);
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در علامت‌گذاری نوتیفیکیشن‌ها', { variant: 'error' });
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const response = await notificationApi.deleteMultipleNotifications(selectedNotifications);
      if (response.success) {
        enqueueSnackbar(`${response.data?.deleted || 0} نوتیفیکیشن حذف شد`, { variant: 'success' });
        setSelectedNotifications([]);
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در حذف نوتیفیکیشن‌ها', { variant: 'error' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationApi.markAllAsRead('admin_panel');
      if (response.success) {
        enqueueSnackbar(`${response.data?.updated || 0} نوتیفیکیشن به عنوان خوانده شده علامت‌گذاری شد`, { variant: 'success' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در علامت‌گذاری همه نوتیفیکیشن‌ها', { variant: 'error' });
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await notificationApi.clearAllNotifications('admin_panel');
      if (response.success) {
        enqueueSnackbar(`${response.data?.deleted || 0} نوتیفیکیشن حذف شد`, { variant: 'success' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در حذف همه نوتیفیکیشن‌ها', { variant: 'error' });
    }
  };

  const handleExport = async () => {
    try {
      const blob = await notificationApi.exportNotifications('admin_panel', {
        type: selectedType !== 'all' ? selectedType : undefined,
        isRead: selectedStatus === 'all' ? undefined : selectedStatus === 'read'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notifications-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      enqueueSnackbar('گزارش با موفقیت دانلود شد', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleViewDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailsOpen(true);
  };

  const handleCreateNotification = async () => {
    try {
      if (!createForm.targetUserId || !createForm.title || !createForm.message || !createForm.type) {
        enqueueSnackbar('لطفاً تمام فیلدهای الزامی را پر کنید', { variant: 'error' });
        return;
      }

      const notificationData = {
        targetUserId: createForm.targetUserId,
        title: createForm.title,
        message: createForm.message,
        type: createForm.type,
        data: createForm.data ? JSON.parse(createForm.data) : undefined
      };

      const response = await notificationApi.createNotification(notificationData);
      if (response.success) {
        enqueueSnackbar('نوتیفیکیشن با موفقیت ایجاد شد', { variant: 'success' });
        setCreateDialogOpen(false);
        setCreateForm({ targetUserId: '', title: '', message: '', type: '', data: '' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در ایجاد نوتیفیکیشن', { variant: 'error' });
    }
  };

  const handleSendBulkNotification = async () => {
    try {
      if (!bulkForm.title || !bulkForm.message || !bulkForm.type) {
        enqueueSnackbar('لطفاً تمام فیلدهای الزامی را پر کنید', { variant: 'error' });
        return;
      }

      const notificationData = {
        targetRole: bulkForm.targetRole,
        title: bulkForm.title,
        message: bulkForm.message,
        type: bulkForm.type,
        data: bulkForm.data ? JSON.parse(bulkForm.data) : undefined
      };

      const response = await notificationApi.sendBulkNotifications(notificationData);
      if (response.success) {
        enqueueSnackbar(`${response.data?.sent || 0} نوتیفیکیشن ارسال شد`, { variant: 'success' });
        setBulkDialogOpen(false);
        setBulkForm({ targetRole: 'user', title: '', message: '', type: '', data: '' });
        loadNotifications();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('خطا در ارسال نوتیفیکیشن‌های گروهی', { variant: 'error' });
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      [ADMIN_NOTIFICATION_TYPES.NEW_USER_REGISTERED]: <Avatar sx={{ bgcolor: 'primary.main' }}><PersonIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.NEW_SELLER_APPLICATION]: <Avatar sx={{ bgcolor: 'info.main' }}><StoreIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.SELLER_VERIFICATION_REQUEST]: <Avatar sx={{ bgcolor: 'warning.main' }}><VerifiedIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.NEW_PRODUCT_PENDING]: <Avatar sx={{ bgcolor: 'secondary.main' }}><InventoryIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.NEW_WITHDRAWAL_REQUEST]: <Avatar sx={{ bgcolor: 'success.main' }}><PaymentIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.SYSTEM_ERROR]: <Avatar sx={{ bgcolor: 'error.main' }}><ErrorIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.DISPUTE_RAISED]: <Avatar sx={{ bgcolor: 'warning.main' }}><WarningIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.NEW_TICKET]: <Avatar sx={{ bgcolor: 'info.main' }}><SupportIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.TICKET_ESCALATED]: <Avatar sx={{ bgcolor: 'error.main' }}><PriorityHighIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.PAYMENT_ISSUE]: <Avatar sx={{ bgcolor: 'error.main' }}><PaymentIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.SECURITY_ALERT]: <Avatar sx={{ bgcolor: 'error.main' }}><SecurityIcon /></Avatar>,
      [ADMIN_NOTIFICATION_TYPES.BANK_ACCOUNT_VERIFICATION]: <Avatar sx={{ bgcolor: 'primary.main' }}><AccountBalanceIcon /></Avatar>
    };
    return iconMap[type] || <Avatar sx={{ bgcolor: 'grey.500' }}><NotificationsIcon /></Avatar>;
  };

  const getNotificationColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (type.includes('error') || type.includes('dispute') || type.includes('security')) return 'error';
    if (type.includes('warning') || type.includes('escalated')) return 'warning';
    if (type.includes('success') || type.includes('approved')) return 'success';
    if (type.includes('info') || type.includes('ticket')) return 'info';
    return 'primary';
  };

  const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} دقیقه پیش`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} ساعت پیش`;
    } else {
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        مدیریت نوتیفیکیشن‌ها
        </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    کل نوتیفیکیشن‌ها
                  </Typography>
                </Box>
                <NotificationsIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {stats?.unread || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    خوانده نشده
                  </Typography>
                </Box>
                <Badge badgeContent={stats?.unread || 0} color="error">
                  <NotificationsIcon sx={{ fontSize: 40, color: 'error.main' }} />
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats?.read || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    خوانده شده
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {Object.values(stats?.byType || {}).reduce((sum, count) => 
                      sum + (typeof count === 'number' ? count : 0), 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    انواع مختلف
                  </Typography>
                </Box>
                <FilterIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
              size="small"
              placeholder="جستجو در نوتیفیکیشن‌ها..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                )
                }}
              />
            </Grid>
            
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع</InputLabel>
                <Select
                  value={selectedType}
                label="نوع"
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  {notificationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  value={selectedStatus}
                label="وضعیت"
                  onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'read' | 'unread')}
                >
                  <MenuItem value="all">همه</MenuItem>
                  <MenuItem value="unread">خوانده نشده</MenuItem>
                  <MenuItem value="read">خوانده شده</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
          <Grid item xs={12} sm={6} md={5}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadNotifications}
                disabled={loading}
              >
                بروزرسانی
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
              >
                دانلود
              </Button>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                ایجاد
              </Button>

              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setBulkDialogOpen(true)}
              >
                ارسال گروهی
              </Button>

              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={selectedNotifications.length === 0}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

      {/* Bulk Actions */}
      {selectedNotifications.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">
                {selectedNotifications.length} نوتیفیکیشن انتخاب شده
              </Typography>
              <Button
                size="small"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkSelectedAsRead}
              >
                علامت‌گذاری به عنوان خوانده شده
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteSelected}
                color="error"
              >
                حذف
              </Button>
        <Button
                size="small"
                onClick={() => setSelectedNotifications([])}
              >
                لغو انتخاب
        </Button>
            </Stack>
      </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Notifications List */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Select All */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
                  checked={notifications.length > 0 && selectedNotifications.length === notifications.length}
                  indeterminate={selectedNotifications.length > 0 && selectedNotifications.length < notifications.length}
              onChange={handleSelectAll}
            />
                <Typography variant="body2">
                  انتخاب همه ({notifications.length})
            </Typography>
          </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<MarkReadIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={!stats?.unread}
                >
                  علامت‌گذاری همه به عنوان خوانده شده
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteAllIcon />}
                  onClick={handleClearAll}
                  color="error"
                  disabled={notifications.length === 0}
                >
                  حذف همه
                </Button>
              </Stack>
            </Stack>
            </Box>

          {/* Notifications */}
          <List>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <ListItem
                  sx={{
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ListItemIcon>
                      <Checkbox
                        checked={selectedNotifications.includes(notification._id)}
                        onChange={() => handleSelectNotification(notification._id)}
                      />
                  </ListItemIcon>
                      
                  <ListItemIcon>
                        {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                      
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                          variant="subtitle1"
                          sx={{ 
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleViewDetails(notification)}
                          >
                            {notification.title}
                          </Typography>
                            <Chip
                          label={notificationTypes.find(t => t.value === notification.type)?.label || notification.type}
                              size="small"
                          color={getNotificationColor(notification.type)}
                          variant="outlined"
                            />
                        {!notification.isRead && (
                          <Chip label="جدید" size="small" color="error" />
                          )}
                        </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.createdAt)}
                            </Typography>
                          </Box>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="مشاهده جزئیات">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(notification)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {notification.isRead ? (
                        <Tooltip title="علامت‌گذاری به عنوان خوانده نشده">
                              <IconButton
                                size="small"
                            onClick={() => handleMarkAsUnread(notification._id)}
                              >
                            <MarkUnreadIcon />
                              </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="علامت‌گذاری به عنوان خوانده شده">
                            <IconButton
                              size="small"
                            onClick={() => handleMarkAsRead(notification._id)}
                            >
                            <MarkReadIcon />
                            </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Empty State */}
          {!loading && notifications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                نوتیفیکیشنی یافت نشد
              </Typography>
              <Typography variant="body2" color="text.secondary">
                هیچ نوتیفیکیشنی با فیلترهای انتخاب شده وجود ندارد
              </Typography>
                          </Box>
          )}
                  </CardContent>
                </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
                    color="primary"
            showFirstButton
            showLastButton
                  />
                </Box>
              )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={handleMarkSelectedAsRead}>
          <MarkReadIcon sx={{ mr: 1 }} />
          علامت‌گذاری به عنوان خوانده شده
        </MenuItem>
        <MenuItem onClick={handleDeleteSelected}>
          <DeleteIcon sx={{ mr: 1 }} />
          حذف انتخاب شده‌ها
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          جزئیات نوتیفیکیشن
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getNotificationIcon(selectedNotification.type)}
                    <Box>
                      <Typography variant="h6">
                        {selectedNotification.title}
                      </Typography>
                      <Chip
                        label={notificationTypes.find(t => t.value === selectedNotification.type)?.label || selectedNotification.type}
                        size="small"
                        color={getNotificationColor(selectedNotification.type)}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedNotification.message}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    تاریخ ایجاد: {formatDate(selectedNotification.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    وضعیت: {selectedNotification.isRead ? 'خوانده شده' : 'خوانده نشده'}
                  </Typography>
                </Grid>

                {selectedNotification.data && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      اطلاعات اضافی:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedNotification.data, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            بستن
          </Button>
          {selectedNotification && !selectedNotification.isRead && (
            <Button
              variant="contained"
              onClick={() => {
                handleMarkAsRead(selectedNotification._id);
                setDetailsOpen(false);
              }}
            >
              علامت‌گذاری به عنوان خوانده شده
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Notification Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          ایجاد نوتیفیکیشن جدید
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="گیرنده"
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                value={createForm.targetUserId}
                onChange={(e) => setCreateForm(prev => ({ ...prev, targetUserId: e.target.value }))}
                required
                helperText="شمارهٔ موبایل، ایمیل یا شناسهٔ کاربر — هر کدام را داشتید"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>نوع نوتیفیکیشن</InputLabel>
                <Select
                  value={createForm.type}
                  label="نوع نوتیفیکیشن"
                  onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value }))}
                >
                  {createNotificationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="عنوان"
                value={createForm.title}
                onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="پیام"
                value={createForm.message}
                onChange={(e) => setCreateForm(prev => ({ ...prev, message: e.target.value }))}
                multiline
                rows={4}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="داده‌های اضافی (JSON)"
                value={createForm.data}
                onChange={(e) => setCreateForm(prev => ({ ...prev, data: e.target.value }))}
                multiline
                rows={3}
                helperText="داده‌های اضافی به فرمت JSON (اختیاری)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            لغو
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNotification}
          >
            ایجاد نوتیفیکیشن
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Notification Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          ارسال نوتیفیکیشن گروهی
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>گروه هدف</InputLabel>
                <Select
                  value={bulkForm.targetRole}
                  label="گروه هدف"
                  onChange={(e) => setBulkForm(prev => ({ ...prev, targetRole: e.target.value as 'user' | 'seller' | 'admin' }))}
                >
                  <MenuItem value="user">کاربران</MenuItem>
                  <MenuItem value="seller">فروشندگان</MenuItem>
                  <MenuItem value="admin">ادمین‌ها</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>نوع نوتیفیکیشن</InputLabel>
                <Select
                  value={bulkForm.type}
                  label="نوع نوتیفیکیشن"
                  onChange={(e) => setBulkForm(prev => ({ ...prev, type: e.target.value }))}
                >
                  {createNotificationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="عنوان"
                value={bulkForm.title}
                onChange={(e) => setBulkForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="پیام"
                value={bulkForm.message}
                onChange={(e) => setBulkForm(prev => ({ ...prev, message: e.target.value }))}
                multiline
                rows={4}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="داده‌های اضافی (JSON)"
                value={bulkForm.data}
                onChange={(e) => setBulkForm(prev => ({ ...prev, data: e.target.value }))}
                multiline
                rows={3}
                helperText="داده‌های اضافی به فرمت JSON (اختیاری)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>
            لغو
          </Button>
          <Button
            variant="contained"
            onClick={handleSendBulkNotification}
          >
            ارسال گروهی
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsPage; 